import {
  mdiAccount,
  mdiEmailFastOutline,
  mdiMapMarkerOutline,
  mdiOfficeBuilding,
  mdiPhoneOutgoing,
} from "@mdi/js"
import { formatContactAddress } from "../../../../../../../../../_shared/_logic/contact-addresses"
import {
  buildContactPointMetaTooltipFields,
  sortContactPointsByQuality,
} from "../../../../../../../../../_shared/_logic/contact-quality-tags"
import {
  buildRichTooltip,
  tooltipField,
  tooltipTitle,
} from "../../../../../../../../../../../_shared/_logic/rich-tooltips"
import type { CustomerContactTag } from "../customers-table-contract"
import { contactPointKindLabel as sharedContactPointKindLabel } from "../../../../../../../../../_shared/_logic/contact-point-kinds"

export const formatDate = (value: string | null | undefined) => {
  if (!value) return "–"

  const parsed = new Date(value)

  if (Number.isNaN(parsed.getTime())) return "–"

  return parsed.toLocaleDateString("de-DE")
}

export const truncateTagLabel = (value: string | null | undefined, maxLength = 20) => {
  const text = String(value || "").trim()

  if (text.length <= maxLength) return text

  return `${text.slice(0, Math.max(0, maxLength - 3)).trimEnd()}...`
}

export const customerTypeLabel = (type: CustomerType | string | null | undefined) =>
  String(type || "").toUpperCase() === "COMPANY" ? "Unternehmen" : "Privatperson"

export const customerTypeIcon = (type: CustomerType | string | null | undefined) =>
  String(type || "").toUpperCase() === "COMPANY" ? mdiOfficeBuilding : mdiAccount

export const customerTypeColor = (type: CustomerType | string | null | undefined) =>
  String(type || "").toUpperCase() === "COMPANY" ? "blue" : "orange"

export const contactKindLabel = (kind: string | null | undefined) => {
  const label = sharedContactPointKindLabel(kind)

  return label === "Kontaktpunkt" ? "Kontakt" : label
}

export const contactKindIcon = (kind: string | null | undefined) => {
  const normalized = String(kind || "").toUpperCase()

  if (normalized === "EMAIL") return mdiEmailFastOutline

  if (normalized === "ADDRESS") return mdiMapMarkerOutline

  return mdiPhoneOutgoing
}

export const contactKindColor = (kind: string | null | undefined) => {
  const normalized = String(kind || "").toUpperCase()

  if (normalized === "EMAIL") return "blue"

  if (normalized === "ADDRESS") return "amber"

  return "teal"
}

export const isVisibleContactPoint = (point: ContactPoint) =>
  !["CANDIDATE", "STALE_CANDIDATE", "INVALID", "DO_NOT_USE", "ARCHIVED"].includes(
    String(
      (point as ContactPoint & { lifecycle_status?: string | null }).lifecycle_status || "ACTIVE",
    ).toUpperCase(),
  )

export const formatContactValue = (point: ContactPoint) => {
  const rawValue = (point.value ?? point.normalized_value ?? "").trim()

  return String(point.kind).toUpperCase() === "ADDRESS" ? formatContactAddress(rawValue) : rawValue
}

export const buildContactTags = (contactPoints: ContactPoint[]): CustomerContactTag[] =>
  sortContactPointsByQuality(contactPoints ?? [])
    .filter(isVisibleContactPoint)
    .map((point): CustomerContactTag | null => {
      const label = formatContactValue(point)

      if (!label) return null

      const kind = String(point.kind || "").toUpperCase()
      const metaFields = buildContactPointMetaTooltipFields(point)

      return {
        id: point.id || `${kind}:${label}`,
        label: kind === "ADDRESS" ? "Adresse" : label,
        kind,
        tooltip: buildRichTooltip([
          kind === "ADDRESS" ? tooltipField("Postadresse", label) : null,
          ...metaFields.map((field) => tooltipField(field.label, field.value)),
        ]),
        tooltipHtml: true,
      }
    })
    .filter((tag): tag is CustomerContactTag => Boolean(tag))

export const buildContactPointsByCustomerId = (contactPoints: ContactPoint[]) =>
  (contactPoints ?? []).reduce(
    (acc, point) => {
      const customerId = (point.customer_id ?? "").trim()

      if (!customerId) return acc

      const list = acc[customerId] ?? []
      list.push(point)
      acc[customerId] = list

      return acc
    },
    {} as Record<string, ContactPoint[]>,
  )
